import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  VendorLookupSearchResult,
  VendorMigrationExecuteResponse,
  VendorMigrationProductCandidate,
  VendorMigrationProductResolution,
  VendorMigrationPreviewResponse
} from 'sb/api/vendor';

type UseVendorMigrationArgs = {
  sourceVendorId?: number;
  searchMigrationTargetProducts: (query: {
    target_vendor_id: number;
    str: string;
  }) => Promise<{ products: VendorMigrationProductCandidate[] }>;
};

export const useVendorMigration = ({ sourceVendorId, searchMigrationTargetProducts }: UseVendorMigrationArgs) => {
  const [migrationTargetSearchTerm, setMigrationTargetSearchTerm] = useState('');
  const [migrationTargetSearchLoading, setMigrationTargetSearchLoading] = useState(false);
  const [migrationTargetSearchResults, setMigrationTargetSearchResults] = useState<VendorLookupSearchResult[]>([]);
  const [selectedMigrationTargetVendor, setSelectedMigrationTargetVendor] = useState<VendorLookupSearchResult>();
  const [migrationPreviewLoading, setMigrationPreviewLoading] = useState(false);
  const [migrationExecuteLoading, setMigrationExecuteLoading] = useState(false);
  const [migrationPreview, setMigrationPreview] = useState<VendorMigrationPreviewResponse | null>(null);
  const [migrationExecuteResult, setMigrationExecuteResult] = useState<VendorMigrationExecuteResponse | null>(null);
  const [migrationResolutionsByProductId, setMigrationResolutionsByProductId] = useState<
    Record<number, VendorMigrationProductResolution>
  >({});
  const [migrationManualSearchTermsByProductId, setMigrationManualSearchTermsByProductId] = useState<
    Record<number, string>
  >({});
  const [migrationManualSearchLoadingByProductId, setMigrationManualSearchLoadingByProductId] = useState<
    Record<number, boolean>
  >({});
  const [migrationManualSearchResultsByProductId, setMigrationManualSearchResultsByProductId] = useState<
    Record<number, VendorMigrationProductCandidate[]>
  >({});
  const [isExecuteMigrationModalOpen, setIsExecuteMigrationModalOpen] = useState(false);
  const [isAutoMatchedProductsExpanded, setIsAutoMatchedProductsExpanded] = useState(false);
  const migrationManualSearchRequestIdsRef = useRef<Record<number, number>>({});
  const searchMigrationTargetProductsRef = useRef(searchMigrationTargetProducts);

  useEffect(() => {
    searchMigrationTargetProductsRef.current = searchMigrationTargetProducts;
  }, [searchMigrationTargetProducts]);

  const clearMigrationRunState = () => {
    setMigrationPreview(null);
    setMigrationExecuteResult(null);
    setMigrationResolutionsByProductId({});
    setMigrationManualSearchTermsByProductId({});
    setMigrationManualSearchLoadingByProductId({});
    setMigrationManualSearchResultsByProductId({});
    migrationManualSearchRequestIdsRef.current = {};
    setIsExecuteMigrationModalOpen(false);
    setIsAutoMatchedProductsExpanded(false);
  };

  useEffect(() => {
    clearMigrationRunState();
    if (sourceVendorId && selectedMigrationTargetVendor?.id === sourceVendorId) {
      setSelectedMigrationTargetVendor(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceVendorId, selectedMigrationTargetVendor?.id]);

  const handleDebouncedManualTargetProductSearch = useMemo(
    () =>
      debounce(async (sourceProductId: number, str: string, targetVendorId?: number) => {
        const trimmedSearchTerm = str.trim();

        if (!targetVendorId || trimmedSearchTerm.length < 2) {
          setMigrationManualSearchResultsByProductId((previousResults) => ({
            ...previousResults,
            [sourceProductId]: []
          }));
          setMigrationManualSearchLoadingByProductId((previousLoading) => ({
            ...previousLoading,
            [sourceProductId]: false
          }));
          return;
        }

        const requestId = (migrationManualSearchRequestIdsRef.current[sourceProductId] || 0) + 1;
        migrationManualSearchRequestIdsRef.current[sourceProductId] = requestId;

        setMigrationManualSearchLoadingByProductId((previousLoading) => ({
          ...previousLoading,
          [sourceProductId]: true
        }));
        try {
          const response = await searchMigrationTargetProductsRef.current({
            target_vendor_id: targetVendorId,
            str: trimmedSearchTerm
          });
          if (migrationManualSearchRequestIdsRef.current[sourceProductId] !== requestId) {
            return;
          }
          setMigrationManualSearchResultsByProductId((previousResults) => ({
            ...previousResults,
            [sourceProductId]: response.products
          }));
        } catch {
          if (migrationManualSearchRequestIdsRef.current[sourceProductId] !== requestId) {
            return;
          }
          setMigrationManualSearchResultsByProductId((previousResults) => ({
            ...previousResults,
            [sourceProductId]: []
          }));
        } finally {
          if (migrationManualSearchRequestIdsRef.current[sourceProductId] === requestId) {
            setMigrationManualSearchLoadingByProductId((previousLoading) => ({
              ...previousLoading,
              [sourceProductId]: false
            }));
          }
        }
      }, 250),
    []
  );

  const handleManualTargetProductTypeahead = (sourceProductId: number, rawValue: string) => {
    setMigrationManualSearchTermsByProductId((previousTerms) => ({
      ...previousTerms,
      [sourceProductId]: rawValue
    }));
    handleDebouncedManualTargetProductSearch(sourceProductId, rawValue, selectedMigrationTargetVendor?.id);
  };

  useEffect(() => {
    return () => {
      handleDebouncedManualTargetProductSearch.cancel();
    };
  }, [handleDebouncedManualTargetProductSearch]);

  return {
    migrationTargetSearchTerm,
    migrationTargetSearchLoading,
    migrationTargetSearchResults,
    selectedMigrationTargetVendor,
    migrationPreviewLoading,
    migrationExecuteLoading,
    migrationPreview,
    migrationExecuteResult,
    migrationResolutionsByProductId,
    migrationManualSearchTermsByProductId,
    migrationManualSearchLoadingByProductId,
    migrationManualSearchResultsByProductId,
    isExecuteMigrationModalOpen,
    isAutoMatchedProductsExpanded,
    setMigrationTargetSearchTerm,
    setMigrationTargetSearchLoading,
    setMigrationTargetSearchResults,
    setSelectedMigrationTargetVendor,
    setMigrationPreviewLoading,
    setMigrationExecuteLoading,
    setMigrationPreview,
    setMigrationExecuteResult,
    setMigrationResolutionsByProductId,
    setMigrationManualSearchTermsByProductId,
    setMigrationManualSearchLoadingByProductId,
    setMigrationManualSearchResultsByProductId,
    setIsExecuteMigrationModalOpen,
    setIsAutoMatchedProductsExpanded,
    clearMigrationRunState,
    handleManualTargetProductTypeahead
  };
};
